import React, { useCallback, useEffect, useMemo, useState } from 'react';

const EMPTY_DATA = {
  events: [],
  clusters: [],
  audit: [],
  baseline: null,
};

export const normalizeRecognitionEvent = (event = {}, index = 0) => {
  const id = event.id || event.eventId || event.event_id || `behaviour-${index}`;
  const reason = event.reason || event.reasonCode || event.reason_code || event.kind || 'review_required';
  const objectLabel = event.objectLabel || event.object_label || event.label || event.object || 'object';
  const feed = event.feedName || event.feed_name || event.feed || event.cameraName || event.camera_name || 'Unknown feed';
  const zone = event.zone || event.room || event.area || 'Unmapped zone';
  const timestamp = event.timestamp || event.createdAt || event.created_at || event.startTime || event.start_time || null;
  const explanation = event.explanation || event.explanationSummary || event.explanation_summary
    || `${String(reason).replace(/_/g, ' ')} for ${objectLabel} at ${zone}.`;

  return {
    id,
    reason,
    objectLabel,
    feed,
    zone,
    timestamp,
    severity: event.severity || event.priority || 'review',
    reviewState: event.reviewState || event.review_state || event.state || 'unreviewed',
    explanation,
    scheduleContext: event.scheduleContext || event.schedule_context || event.context || '',
    mediaRestricted: Boolean(event.mediaRestricted ?? event.media_restricted ?? event.restrictedMedia),
    trackId: event.trackId || event.track_id || event.track?.id || '',
    clusterId: event.clusterId || event.cluster_id || event.identityClusterId || event.identity_cluster_id || '',
    candidateId: event.candidateId || event.candidate_id || event.faceCandidateId || event.face_candidate_id || '',
    track: event.track || null,
    cluster: event.cluster || null,
    faceCandidate: event.faceCandidate || event.face_candidate || null,
    evidenceRef: event.evidenceRef || event.evidence_ref || event.frigateEventId || event.frigate_event_id || id,
  };
};

export const normalizeIdentityCluster = (cluster = {}, index = 0) => ({
  id: cluster.id || cluster.clusterId || cluster.cluster_id || `cluster-${index}`,
  label: cluster.label || cluster.name || cluster.clusterLabel || cluster.cluster_label || `identity cluster ${index + 1}`,
  objectClass: cluster.objectClass || cluster.object_class || cluster.objectLabel || cluster.object_label || 'object',
  state: cluster.state || cluster.feedbackState || cluster.feedback_state || 'unknown_candidate',
  firstSeen: cluster.firstSeen || cluster.first_seen || '',
  lastSeen: cluster.lastSeen || cluster.last_seen || '',
  occurrenceCount: cluster.occurrenceCount || cluster.occurrence_count || cluster.occurrences || 0,
  confidence: cluster.confidence ?? cluster.matchConfidence ?? cluster.match_confidence ?? null,
  modelId: cluster.modelId || cluster.model_id || cluster.provider || 'model pending',
  summary: cluster.summary || cluster.description || 'Probably the same recurring object, pending admin review.',
});

export const normalizeTrackDetail = (track = {}, fallbackId = '') => ({
  id: track.id || track.trackId || track.track_id || fallbackId || 'track pending',
  objectLabel: track.objectLabel || track.object_label || track.label || 'object',
  feed: track.feedName || track.feed_name || track.feed || 'Unknown feed',
  zone: track.zone || track.room || 'Unmapped zone',
  startTime: track.startTime || track.start_time || '',
  endTime: track.endTime || track.end_time || '',
  movement: track.movement || track.movementSummary || track.movement_summary || 'Movement summary pending',
  frigateEventId: track.frigateEventId || track.frigate_event_id || 'not linked',
  modelId: track.modelId || track.model_id || track.provider || 'tracker pending',
  status: track.status || track.processingStatus || track.processing_status || 'probabilistic',
});

export const normalizeFaceCandidate = (candidate = {}, fallbackId = '') => ({
  id: candidate.id || candidate.candidateId || candidate.candidate_id || fallbackId || 'face candidate pending',
  observationId: candidate.observationId || candidate.observation_id || '',
  candidateLabel: candidate.candidateLabel || candidate.candidate_label || candidate.label || 'Unknown candidate',
  confirmedIdentity: candidate.confirmedIdentity || candidate.confirmed_identity || '',
  identityRole: candidate.identityRole || candidate.identity_role || candidate.role || 'unknown_candidate',
  reviewState: candidate.reviewState || candidate.review_state || candidate.status || 'candidate',
  score: candidate.score ?? candidate.confidence ?? null,
  authorized: Boolean(candidate.authorized ?? candidate.mediaAuthorized ?? candidate.media_authorized),
  restricted: Boolean(candidate.restricted ?? candidate.mediaRestricted ?? candidate.media_restricted ?? true),
  contextSignals: candidate.contextSignals || candidate.context_signals || 'Shown with schedule, track, and zone context only.',
  areaPolicy: candidate.areaPolicy || candidate.area_policy || candidate.allowedArea || candidate.allowed_area || '',
  schedulePolicy: candidate.schedulePolicy || candidate.schedule_policy || candidate.allowedSchedule || candidate.allowed_schedule || '',
  mergeTargets: toArray(candidate.mergeTargets || candidate.merge_targets || candidate.similarCandidates || candidate.similar_candidates, [])
    .map((target, index) => ({
      id: target.id || target.candidateId || target.candidate_id || `merge-target-${index}`,
      label: target.label || target.candidateLabel || target.candidate_label || target.name || `Candidate ${index + 1}`,
    })),
});

const toArray = (payload, keys = []) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) {
      return payload[key];
    }
  }
  return [];
};

const unwrap = (response) => response?.data?.data ?? response?.data ?? response ?? null;

const formatDate = (value) => {
  if (!value) {
    return 'Time pending';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatConfidence = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Confidence pending';
  }
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return String(value);
  }
  return `${Math.round(number * 100)}% probable`;
};

function PrivacySafeMedia({ restricted, label }) {
  if (restricted) {
    return (
      <div className="panoptic-privacy-placeholder" data-testid="T_PANOPTIC_WEB_122_restricted_media">
        <strong>Restricted media</strong>
        <span>{label || 'Media is hidden until policy and role allow access.'}</span>
      </div>
    );
  }

  return (
    <div className="panoptic-media-placeholder">
      <strong>Authorized thumbnail</strong>
      <span>Preview requested through the protected media endpoint only.</span>
    </div>
  );
}

function FeedbackActions({ disabled, onDecision }) {
  const actions = [
    ['familiar_allowed', 'Familiar'],
    ['watch_more', 'Watch more'],
    ['false_positive', 'False positive'],
    ['merge_candidate', 'Merge'],
    ['split_identity', 'Split'],
  ];

  return (
    <div className="panoptic-feedback-actions" data-testid="T_PANOPTIC_WEB_118_feedback_actions">
      {actions.map(([decision, label]) => (
        <button key={decision} type="button" disabled={disabled} onClick={() => onDecision(decision)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function FaceIdentityAdminPanel({ candidate, disabled, onFaceDecision }) {
  const detail = normalizeFaceCandidate(candidate);
  const [identityRole, setIdentityRole] = useState(detail.identityRole);
  const [areaPolicy, setAreaPolicy] = useState(detail.areaPolicy);
  const [schedulePolicy, setSchedulePolicy] = useState(detail.schedulePolicy);
  const [mergeTargetId, setMergeTargetId] = useState(detail.mergeTargets[0]?.id || '');

  const submitIdentity = () => onFaceDecision?.('identity_role_policy', detail, {
    identity_role: identityRole,
    area_policy: areaPolicy,
    schedule_policy: schedulePolicy,
  });

  const rejectCandidate = () => onFaceDecision?.('rejected_match', detail, {
    identity_role: 'rejected_match',
  });

  const mergeCandidate = () => onFaceDecision?.('merge_candidate', detail, {
    merge_target_id: mergeTargetId,
  });

  return (
    <div className="panoptic-face-admin">
      <section className="panoptic-face-admin-card" data-testid="T-PAN-FACE-011_staff_policy">
        <h4>Identity policy</h4>
        <p>Human review is required before a candidate becomes an allowed or blocked identity.</p>
        <label>
          <span>Identity role</span>
          <select value={identityRole} onChange={(event) => setIdentityRole(event.target.value)} disabled={disabled}>
            <option value="staff_allowed">Staff allowed</option>
            <option value="known_visitor">Known visitor</option>
            <option value="contractor">Contractor</option>
            <option value="blocked_person">Blocked person</option>
            <option value="unknown_candidate">Unknown candidate</option>
          </select>
        </label>
        <label>
          <span>Area policy</span>
          <input
            value={areaPolicy}
            onChange={(event) => setAreaPolicy(event.target.value)}
            disabled={disabled}
            placeholder="Allowed feeds, doors, or zones"
          />
        </label>
        <label>
          <span>Schedule policy</span>
          <input
            value={schedulePolicy}
            onChange={(event) => setSchedulePolicy(event.target.value)}
            disabled={disabled}
            placeholder="Class, booking, or shift window"
          />
        </label>
        <button type="button" disabled={disabled} onClick={submitIdentity}>
          Save identity policy
        </button>
      </section>

      <section className="panoptic-face-admin-card" data-testid="T-PAN-FACE-012_candidate_controls">
        <h4>Candidate decision</h4>
        <p>Rejecting or merging preserves the original observation, restricted evidence reference, and audit trail.</p>
        <label>
          <span>Merge target</span>
          <select value={mergeTargetId} onChange={(event) => setMergeTargetId(event.target.value)} disabled={disabled}>
            <option value="">Select candidate</option>
            {detail.mergeTargets.map((target) => (
              <option key={target.id} value={target.id}>{target.label}</option>
            ))}
          </select>
        </label>
        <div className="panoptic-face-actions">
          <button type="button" disabled={disabled} onClick={rejectCandidate}>Reject candidate</button>
          <button type="button" disabled={disabled || !mergeTargetId} onClick={mergeCandidate}>Merge candidate</button>
        </div>
      </section>
    </div>
  );
}

function TrackPanel({ track }) {
  const detail = normalizeTrackDetail(track);
  return (
    <section className="panoptic-review-detail-card" data-testid="T_PANOPTIC_WEB_115_track_drawer">
      <h3>Track detail</h3>
      <p>Tracker IDs are probabilistic and are not confirmed identity.</p>
      <dl>
        <div><dt>Track</dt><dd>{detail.id}</dd></div>
        <div><dt>Object</dt><dd>{detail.objectLabel}</dd></div>
        <div><dt>Feed / zone</dt><dd>{detail.feed} / {detail.zone}</dd></div>
        <div><dt>Time</dt><dd>{formatDate(detail.startTime)} to {formatDate(detail.endTime)}</dd></div>
        <div><dt>Movement</dt><dd>{detail.movement}</dd></div>
        <div><dt>Frigate event</dt><dd>{detail.frigateEventId}</dd></div>
        <div><dt>Model</dt><dd>{detail.modelId}</dd></div>
        <div><dt>Status</dt><dd>{detail.status}</dd></div>
      </dl>
    </section>
  );
}

function ClusterPanel({ cluster }) {
  const detail = normalizeIdentityCluster(cluster);
  return (
    <section className="panoptic-review-detail-card" data-testid="T_PANOPTIC_WEB_116_cluster_drawer">
      <h3>Identity cluster</h3>
      <p>{detail.summary}</p>
      <dl>
        <div><dt>Cluster</dt><dd>{detail.label}</dd></div>
        <div><dt>Class</dt><dd>{detail.objectClass}</dd></div>
        <div><dt>State</dt><dd>{detail.state}</dd></div>
        <div><dt>First seen</dt><dd>{formatDate(detail.firstSeen)}</dd></div>
        <div><dt>Last seen</dt><dd>{formatDate(detail.lastSeen)}</dd></div>
        <div><dt>Occurrences</dt><dd>{detail.occurrenceCount}</dd></div>
        <div><dt>Match</dt><dd>{formatConfidence(detail.confidence)}</dd></div>
        <div><dt>Model</dt><dd>{detail.modelId}</dd></div>
      </dl>
    </section>
  );
}

function FacePanel({ candidate, disabled, onFaceDecision }) {
  const detail = normalizeFaceCandidate(candidate);
  const restrictMedia = detail.restricted && !detail.authorized;
  return (
    <section className="panoptic-review-detail-card" data-testid="T_PANOPTIC_WEB_117_face_candidate_drawer">
      <h3>Face candidate</h3>
      <PrivacySafeMedia restricted={restrictMedia} />
      <dl>
        <div><dt>Observation</dt><dd>{detail.observationId || 'Observation pending'}</dd></div>
        <div><dt>Candidate match</dt><dd>{detail.candidateLabel}</dd></div>
        <div><dt>Confirmed identity</dt><dd>{detail.confirmedIdentity || 'Not confirmed'}</dd></div>
        <div><dt>Identity role</dt><dd>{detail.identityRole}</dd></div>
        <div><dt>Review state</dt><dd>{detail.reviewState}</dd></div>
        <div><dt>Score</dt><dd>{formatConfidence(detail.score)}</dd></div>
        <div><dt>Context</dt><dd>{detail.contextSignals}</dd></div>
      </dl>
      <p>No alert is justified by face match alone.</p>
      <FaceIdentityAdminPanel candidate={detail} disabled={disabled} onFaceDecision={onFaceDecision} />
    </section>
  );
}

function BaselinePanel({ baseline, onClose }) {
  const detail = baseline || {};
  return (
    <section className="panoptic-review-detail-card" data-testid="T_PANOPTIC_WEB_120_baseline_settings">
      <header className="panoptic-inline-head">
        <h3>Baseline settings</h3>
        <button type="button" onClick={onClose}>Close</button>
      </header>
      <dl>
        <div><dt>Learning window</dt><dd>{detail.learningWindow || detail.learning_window || 'Not configured'}</dd></div>
        <div><dt>After hours</dt><dd>{detail.afterHours || detail.after_hours || '10pm to 8am'}</dd></div>
        <div><dt>Suppression</dt><dd>{detail.suppressionMode || detail.suppression_mode || 'Audit familiar activity before suppressing'}</dd></div>
      </dl>
    </section>
  );
}

export function PanopticRecognitionReview({
  orgId,
  site,
  api,
  initialData,
  initialSelection,
  onSelectionChange,
}) {
  const [data, setData] = useState(initialData || EMPTY_DATA);
  const [loading, setLoading] = useState(!initialData && Boolean(orgId));
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [objectFilter, setObjectFilter] = useState('all');
  const [selection, setSelection] = useState(initialSelection || { type: '', id: '' });
  const [decisionState, setDecisionState] = useState('');

  const loadReview = useCallback(async () => {
    if (!api || !orgId || initialData) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [eventsResult, clustersResult, baselineResult, auditResult] = await Promise.allSettled([
        api.getBehaviourEvents(orgId, { limit: 50 }),
        api.getIdentityClusters(orgId, { limit: 50 }),
        api.getBaselineSettings(orgId),
        api.getReviewAudit(orgId, { limit: 20 }),
      ]);

      const eventsPayload = eventsResult.status === 'fulfilled' ? unwrap(eventsResult.value) : null;
      const clustersPayload = clustersResult.status === 'fulfilled' ? unwrap(clustersResult.value) : null;
      const baselinePayload = baselineResult.status === 'fulfilled' ? unwrap(baselineResult.value) : null;
      const auditPayload = auditResult.status === 'fulfilled' ? unwrap(auditResult.value) : null;

      setData({
        events: toArray(eventsPayload, ['events', 'items', 'results']).map(normalizeRecognitionEvent),
        clusters: toArray(clustersPayload, ['clusters', 'items', 'results']).map(normalizeIdentityCluster),
        baseline: baselinePayload,
        audit: toArray(auditPayload, ['audit', 'items', 'results']),
      });

      const rejected = [eventsResult, clustersResult, baselineResult, auditResult].find((result) => result.status === 'rejected');
      if (rejected) {
        setError('Recognition review is partially degraded. Available review data is shown.');
      }
    } catch (loadError) {
      setError('Recognition review data is not available from the live backend yet.');
      setData(EMPTY_DATA);
    } finally {
      setLoading(false);
    }
  }, [api, initialData, orgId]);

  useEffect(() => {
    loadReview();
  }, [loadReview]);

  const events = useMemo(() => (data.events || []).map(normalizeRecognitionEvent), [data.events]);
  const clusters = useMemo(() => (data.clusters || []).map(normalizeIdentityCluster), [data.clusters]);

  const filteredEvents = useMemo(() => events.filter((event) => {
    const haystack = [
      event.reason,
      event.objectLabel,
      event.feed,
      event.zone,
      event.reviewState,
      event.explanation,
      event.clusterId,
      event.trackId,
    ].join(' ').toLowerCase();
    const matchesQuery = !query || haystack.includes(query.toLowerCase());
    const matchesObject = objectFilter === 'all' || event.objectLabel === objectFilter;
    return matchesQuery && matchesObject;
  }), [events, objectFilter, query]);

  const selectedEvent = events.find((event) => (
    (selection.type === 'anomaly' && event.id === selection.id)
    || (selection.type === 'track' && event.trackId === selection.id)
    || (selection.type === 'cluster' && event.clusterId === selection.id)
    || (selection.type === 'candidate' && event.candidateId === selection.id)
  )) || filteredEvents[0] || null;
  const selectedCluster = clusters.find((cluster) => (
    (selection.type === 'cluster' && cluster.id === selection.id)
    || selectedEvent?.clusterId === cluster.id
  )) || selectedEvent?.cluster || clusters[0] || null;
  const selectedTrack = selectedEvent?.track || (selectedEvent?.trackId ? { id: selectedEvent.trackId, objectLabel: selectedEvent.objectLabel, feed: selectedEvent.feed, zone: selectedEvent.zone } : null);
  const selectedFace = selectedEvent?.faceCandidate || (selectedEvent?.candidateId ? { id: selectedEvent.candidateId } : null);

  const select = (type, id) => {
    const next = { type, id };
    setSelection(next);
    onSelectionChange?.(next);
  };

  const submitDecision = async (decision) => {
    if (!selectedEvent) {
      return;
    }
    setDecisionState('Saving decision');
    try {
      await api?.postReviewDecision?.(orgId, {
        target_type: selection.type || 'anomaly',
        target_id: selection.id || selectedEvent.id,
        source_event_id: selectedEvent.id,
        evidence_ref: selectedEvent.evidenceRef,
        decision,
      });
      setDecisionState(`Decision saved: ${decision.replace(/_/g, ' ')}`);
    } catch (decisionError) {
      setDecisionState('Decision could not be saved on the live backend.');
    }
  };

  const submitFaceDecision = async (decision, candidate, extra = {}) => {
    if (!selectedEvent) {
      return;
    }
    const detail = normalizeFaceCandidate(candidate, selectedEvent.candidateId);
    setDecisionState('Saving face review');
    try {
      await api?.postReviewDecision?.(orgId, {
        target_type: 'face_candidate',
        target_id: detail.id || selectedEvent.candidateId || selectedEvent.id,
        source_event_id: selectedEvent.id,
        evidence_ref: selectedEvent.evidenceRef,
        decision,
        ...extra,
      });
      setDecisionState(`Face review saved: ${decision.replace(/_/g, ' ')}`);
    } catch (decisionError) {
      setDecisionState('Face review could not be saved on the live backend.');
    }
  };

  return (
    <section className="panoptic-tracking-workspace" data-testid="T_PANOPTIC_WEB_113_tracking_root">
      <div className="panoptic-tracking-toolbar" data-testid="T_PANOPTIC_WEB_121_tracking_search">
        <label>
          <span>Search</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Object, feed, cluster, reason"
            aria-label="Search recognition review"
          />
        </label>
        <label>
          <span>Object</span>
          <select value={objectFilter} onChange={(event) => setObjectFilter(event.target.value)} aria-label="Object filter">
            <option value="all">All objects</option>
            <option value="person">People</option>
            <option value="car">Cars</option>
            <option value="cat">Cats</option>
            <option value="dog">Dogs</option>
          </select>
        </label>
        <button type="button" onClick={() => select('settings', 'baseline')}>Baseline</button>
      </div>

      {error && <div className="panoptic-inline-warning">{error}</div>}

      <div className="panoptic-tracking-grid" data-testid="T_PANOPTIC_WEB_124_responsive_layout">
        <aside className="panoptic-behaviour-inbox" data-testid="T_PANOPTIC_WEB_114_behaviour_inbox">
          <div className="panoptic-inline-head">
            <div>
              <h2>Needs review</h2>
              <p>{site || 'Current scope'}</p>
            </div>
            <span>{filteredEvents.length}</span>
          </div>
          {loading ? (
            <div className="panoptic-inline-state">Loading recognition review</div>
          ) : filteredEvents.length === 0 ? (
            <div className="panoptic-inline-state">No unusual behaviour is waiting for review.</div>
          ) : filteredEvents.map((event) => (
            <button
              className="panoptic-behaviour-row"
              type="button"
              key={event.id}
              onClick={() => select('anomaly', event.id)}
              data-testid="T_PANOPTIC_WEB_114_behaviour_row"
            >
              <strong>{event.reason}</strong>
              <span>{event.objectLabel} / {event.feed} / {event.zone}</span>
              <small>{formatDate(event.timestamp)} / {event.severity} / {event.reviewState}</small>
              <em>{event.explanation}</em>
            </button>
          ))}
        </aside>

        <main className="panoptic-review-panel">
          {selectedEvent ? (
            <>
              <section className="panoptic-review-detail-card">
                <h2>{selectedEvent.reason}</h2>
                <PrivacySafeMedia restricted={selectedEvent.mediaRestricted} label="Event thumbnail is restricted or pending." />
                <p data-testid="T_PANOPTIC_WEB_119_context_explanation">{selectedEvent.explanation}</p>
                <dl>
                  <div><dt>Object</dt><dd>{selectedEvent.objectLabel}</dd></div>
                  <div><dt>Feed / zone</dt><dd>{selectedEvent.feed} / {selectedEvent.zone}</dd></div>
                  <div><dt>Schedule context</dt><dd>{selectedEvent.scheduleContext || 'No expected occupancy matched this event.'}</dd></div>
                  <div><dt>Evidence</dt><dd>{selectedEvent.evidenceRef}</dd></div>
                </dl>
                <FeedbackActions disabled={!api || !orgId} onDecision={submitDecision} />
                {decisionState && <p className="panoptic-inline-state">{decisionState}</p>}
              </section>

              {selectedTrack && <TrackPanel track={selectedTrack} />}
              {selectedCluster && <ClusterPanel cluster={selectedCluster} />}
              {selectedFace && (
                <FacePanel
                  candidate={selectedFace}
                  disabled={!api || !orgId}
                  onFaceDecision={submitFaceDecision}
                />
              )}
            </>
          ) : (
            <section className="panoptic-review-detail-card">
              <h2>Recognition review</h2>
              <p>Select an unusual behaviour case, track, identity cluster, or face candidate.</p>
            </section>
          )}

          {selection.type === 'settings' && (
            <BaselinePanel baseline={data.baseline} onClose={() => select('', '')} />
          )}
        </main>

        <aside className="panoptic-cluster-list" data-testid="T_PANOPTIC_WEB_116_cluster_list">
          <div className="panoptic-inline-head">
            <h2>Identities</h2>
            <span>{clusters.length}</span>
          </div>
          {clusters.length === 0 ? (
            <div className="panoptic-inline-state">No recurring identity clusters returned yet.</div>
          ) : clusters.map((cluster) => (
            <button type="button" key={cluster.id} onClick={() => select('cluster', cluster.id)}>
              <strong>{cluster.label}</strong>
              <span>{cluster.objectClass} / {cluster.state}</span>
              <small>{formatConfidence(cluster.confidence)}</small>
            </button>
          ))}

          <div className="panoptic-audit-strip" data-testid="T_PANOPTIC_WEB_123_review_audit">
            <strong>Audit</strong>
            <span>{(data.audit || []).length} feedback decisions in view</span>
          </div>
        </aside>
      </div>
    </section>
  );
}

export default PanopticRecognitionReview;
